import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Icon, Segment, Menu, Dropdown } from 'semantic-ui-react';
import request from '../../api';
import { useTranslation } from 'react-i18next';
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";

// Function to validate if parentheses are balanced in a formula
const validateFormulaParentheses = (formula, t) => {
    if (!formula || typeof formula !== 'string') return { isValid: true };

    // Check if it's a formula (starts with =)
    if (!formula.trim().startsWith('=')) return { isValid: true };

    let openCount = 0;
    let closeCount = 0;
    let inString = false;
    let stringChar = null;

    for (let i = 0; i < formula.length; i++) {
        const char = formula[i];
        const prevChar = i > 0 ? formula[i - 1] : '';

        // Handle string literals (skip parentheses inside strings)
        if ((char === '"' || char === "'") && prevChar !== '\\') {
            if (!inString) {
                inString = true;
                stringChar = char;
            } else if (char === stringChar) {
                inString = false;
                stringChar = null;
            }
        }

        // Only count parentheses outside of strings
        if (!inString) {
            if (char === '(') {
                openCount++;
            } else if (char === ')') {
                closeCount++;
            }
        }
    }

    if (openCount !== closeCount) {
        const missingCount = openCount - closeCount;
        const errorMessage = t
            ? t('FormulaErrorUnclosedParenthesis', { count: missingCount })
            : `Formula error: Unclosed parenthesis. Expected ${missingCount} more closing parenthesis(es).`;

        return {
            isValid: false,
            error: errorMessage
        };
    }

    return { isValid: true };
};


const transformData = (responseData) => {
    return responseData?.map((sheet) => ({
        id: sheet.id,
        name: sheet.name,
        celldata: sheet?.data?.flatMap((row, rIndex) =>
            row?.map((cell, cIndex) => {
                if (cell !== null) {
                    return {
                        r: rIndex,
                        c: cIndex,
                        v: cell,
                    };
                }

                return undefined;
            })
                ?.filter((cell) => cell !== undefined),
        ),
        calcChain: sheet?.calcChain?.map((item) => {
            const relatedCell = sheet.data[item.r][item.c];
            return {
                r: item.r,
                c: item.c,
                id: item.id,
                v: relatedCell !== null ? relatedCell : null,
            };
        }),
    }));
};


const SpreadsheetEditor = (props) => {
    const { docUrl, data, onBlur = () => { }, readOnly, iconName = 'save', hideAction = false } = props;
    const ref = useRef();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [innerData, setInnerData] = useState(data?.value ? transformData(data?.value) : [{ name: 'Sheet1', celldata: [{ r: 0, c: 0, v: null }] }]);
    const [hasMounted, setHasMounted] = useState(false);
    const [formulaError, setFormulaError] = useState(null);
    // Internal clipboard for handling rich content copy/paste within the app
    const internalClipboard = useRef(null);
    const resizeTimer = useRef(null);

    const refreshSheetWidth = useCallback(() => {
        if (resizeTimer.current) {
            clearTimeout(resizeTimer.current);
        }

        resizeTimer.current = setTimeout(() => {
            if (ref?.current?.resize) {
                ref.current.resize();
            } else if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('resize'));
            }
            resizeTimer.current = null;
        }, 100);
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (resizeTimer.current) {
                clearTimeout(resizeTimer.current);
            }
        };
    }, []);

    useEffect(() => {
        if (docUrl) {
            setLoading(true);
            request.get(docUrl)
                .then(({ data: res }) => { setInnerData(transformData(res)); setLoading(false); })
                .catch(() => { });
        }
        //eslint-disable-next-line
    }, []);

    // Use ResizeObserver to detect both width and height changes
    useEffect(() => {
        if (!hasMounted) {
            setHasMounted(true);
            return;
        }

        const parentSheet = document.getElementById('parentSheet');
        if (!parentSheet) return;

        let rafId = null;
        let lastWidth = parentSheet.offsetWidth;
        let lastHeight = parentSheet.offsetHeight;

        const resizeObserver = new ResizeObserver((entries) => {
            // Cancel any pending animation frame
            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            // Use requestAnimationFrame to defer the callback and prevent loop
            rafId = requestAnimationFrame(() => {
                const entry = entries[0];
                if (entry) {
                    const { width, height } = entry.contentRect;
                    
                    if (Math.abs(width - lastWidth) > 1 || Math.abs(height - lastHeight) > 1) {
                        lastWidth = width;
                        lastHeight = height;
                        refreshSheetWidth();
                    }
                }
            });
        });

        resizeObserver.observe(parentSheet);

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
        };
    }, [hasMounted, refreshSheetWidth]);

    // Validate formulas while typing
    useEffect(() => {
        if (readOnly || !hasMounted) return;

        let inputElement = null;
        let isValidating = false;

        const attachToInput = () => {
            const activeEl = document.activeElement;
            const parentSheet = document.getElementById('parentSheet');

            if (!parentSheet || !activeEl || !parentSheet.contains(activeEl)) return;

            const isEditable = activeEl.tagName === 'INPUT' ||
                activeEl.tagName === 'TEXTAREA' ||
                activeEl.contentEditable === 'true' ||
                activeEl.isContentEditable;

            if (isEditable && activeEl !== inputElement) {
                // Remove old listeners
                if (inputElement) {
                    inputElement.removeEventListener('input', handleInput);
                    inputElement.removeEventListener('keydown', handleKeyDown);
                }

                // Attach to new input
                inputElement = activeEl;
                inputElement.addEventListener('input', handleInput, true);
                inputElement.addEventListener('keydown', handleKeyDown, true);
            }
        };

        const handleInput = () => {
            setFormulaError(null);
        };

        const validateFormula = (target) => {
            if (!target) return { isValid: true };
            const value = target?.value || target?.textContent || target?.innerText || '';

            if (value && value.trim().startsWith('=')) {
                const validation = validateFormulaParentheses(value, t);
                setFormulaError(validation.isValid ? null : validation.error);
                return validation;
            }
            setFormulaError(null);
            return { isValid: true };
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Enter' || e.key === 'Tab') {
                const validation = validateFormula(e.target);
                if (!validation.isValid) {
                    e.preventDefault();
                    e.stopPropagation();
                    alert(validation.error);
                    setTimeout(() => e.target?.focus(), 50);
                    return false;
                }
            }
        };

        // Intercept clicks on cells to validate before allowing focus change
        const handleCellClick = (e) => {
            // Only validate if we have an active input element
            if (!inputElement || isValidating) return;

            // Check if clicking on a cell (not on the input itself)
            const clickedElement = e.target;
            if (clickedElement === inputElement || inputElement.contains(clickedElement)) {
                return; // Clicking on the input itself, allow it
            }

            // Validate the current input value
            const validation = validateFormula(inputElement);
            if (!validation.isValid) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                isValidating = true;
                alert(validation.error);
                isValidating = false;
                // Refocus the input
                setTimeout(() => {
                    if (inputElement && document.getElementById('parentSheet')?.contains(inputElement)) {
                        inputElement.focus();
                        // For contenteditable, move cursor to end
                        if (inputElement.contentEditable === 'true' || inputElement.isContentEditable) {
                            const range = document.createRange();
                            range.selectNodeContents(inputElement);
                            range.collapse(false);
                            const selection = window.getSelection();
                            selection.removeAllRanges();
                            selection.addRange(range);
                        }
                    }
                }, 50);
                return false;
            }
        };

        // Check for active input periodically
        const intervalId = setInterval(attachToInput, 100);
        document.addEventListener('focusin', attachToInput, true);

        const parentSheet = document.getElementById('parentSheet');
        if (parentSheet) {
            // Use capture phase to intercept before fortune-sheet handles it
            parentSheet.addEventListener('mousedown', handleCellClick, true);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('input', handleInput);
                inputElement.removeEventListener('keydown', handleKeyDown);
            }
            if (parentSheet) {
                parentSheet.removeEventListener('mousedown', handleCellClick, true);
            }
            clearInterval(intervalId);
            document.removeEventListener('focusin', attachToInput, true);
        };
    }, [readOnly, hasMounted, t]);

    const handleChange = (newData) => {
        setInnerData(newData);
        if (data?.setValue) {
            data.setValue(newData);
        }
    };

    // Menu handlers
    const handleMenuClick = (e, { name }) => {
        // Close menu immediately
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        // Safe access to ref.current
        const workbookInstance = ref.current;
        const container = document.getElementsByClassName('fortune-sheet-container')?.[0];

        // Helper to restore focus to the sheet before executing commands
        // This is crucial for execCommand or key events to be picked up by the library
        const focusSheet = () => {
            if (container) {
                // Find the internal canvas or editable element if possible, or focus container
                // FortuneSheet usually creates a container with class 'fortune-sheet-container' or similar
                // We will try to focus the first focusable element or the container itself
                // Setting tabindex allows div to be focused
                // if (!container.getAttribute('tabindex')) {
                //     container.setAttribute('tabindex', '-1');
                // }
                container.focus();
            }
        };

        if (name === 'undo') {
            if (workbookInstance && workbookInstance.handleUndo) {
                workbookInstance.handleUndo();
            }
        } else if (name === 'redo') {
            if (workbookInstance && workbookInstance.handleRedo) {
                workbookInstance.handleRedo();
            }
        } else if (name === 'cut') {
            const selection = workbookInstance.getSelection();
            if (selection && selection.length > 0) {
                const range = selection[0];
                const cells = workbookInstance.getCellsByRange(range);

                // Store full cell objects in internal clipboard for rich paste
                internalClipboard.current = {
                    cells: JSON.parse(JSON.stringify(cells)), // Deep copy
                    rowCount: cells.length,
                    colCount: cells[0]?.length || 0
                };

                const text = cells.map(row =>
                    row.map(cell => {
                        if (!cell) return "";
                        return cell.v !== undefined && cell.v !== null ? cell.v : (cell.m || "");
                    }).join("\t")
                ).join("\n");

                navigator.clipboard.writeText(text).then(() => {
                    // Clear content after copy using batch update
                    const emptyData = cells.map(row => row.map(() => null));
                    workbookInstance.setCellValuesByRange(emptyData, range);
                }).catch(err => {
                    console.error("Cut failed:", err);
                    if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
                        alert(t("Clipboard write permission denied. Please allow clipboard access."));
                    } else {
                        alert(t("Cut failed: ") + err.message);
                    }
                });
            }
        } else if (name === 'copy') {
            const selection = workbookInstance.getSelection();
            if (selection && selection.length > 0) {
                const cells = workbookInstance.getCellsByRange(selection[0]);

                // Store full cell objects in internal clipboard
                internalClipboard.current = {
                    cells: JSON.parse(JSON.stringify(cells)),
                    rowCount: cells.length,
                    colCount: cells[0]?.length || 0
                };

                const text = cells.map(row =>
                    row.map(cell => {
                        if (!cell) return "";
                        return cell.v !== undefined && cell.v !== null ? cell.v : (cell.m || "");
                    }).join("\t")
                ).join("\n");

                navigator.clipboard.writeText(text).then(() => {
                    // Success
                }).catch(err => {
                    console.error("Copy failed:", err);
                    if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
                        alert(t("Clipboard write permission denied. Please allow clipboard access."));
                    } else {
                        alert(t("Copy failed: ") + err.message);
                    }
                });
            }
        } else if (name === 'paste') {
            navigator.clipboard.readText().then(text => {
                const selection = workbookInstance.getSelection();
                if (selection && selection.length > 0) {
                    const startRow = selection[0].row[0];
                    const startCol = selection[0].column[0];

                    // Heuristic: If system clipboard text matches internal clipboard text representation,
                    // use internal clipboard (rich content). Otherwise use text.
                    let useInternal = false;
                    if (internalClipboard.current && text) {
                        const internalText = internalClipboard.current.cells.map(row =>
                            row.map(cell => {
                                if (!cell) return "";
                                return cell.v !== undefined && cell.v !== null ? cell.v : (cell.m || "");
                            }).join("\t")
                        ).join("\n");

                        // Check if similar (ignoring some whitespace diffs or just direct match)
                        if (internalText === text || internalText.trim() === text.trim()) {
                            useInternal = true;
                        }
                    }

                    if (useInternal && internalClipboard.current) {
                        // Paste rich content
                        const { cells, rowCount, colCount } = internalClipboard.current;
                        const endRow = startRow + rowCount - 1;
                        const endCol = startCol + colCount - 1;

                        const range = {
                            row: [startRow, endRow],
                            column: [startCol, endCol]
                        };
                        // Assuming setCellValuesByRange accepts full Cell objects which include styles/formulas
                        workbookInstance.setCellValuesByRange(cells, range);
                    } else {
                        // Paste text (fallback or external content)
                        if (!text) return;
                        const rows = text.split(/\r\n|\n|\r/);
                        if (rows[rows.length - 1] === "") rows.pop();
                        const data = rows.map(row => row.split("\t"));

                        if (data.length === 0) return;

                        const endRow = startRow + data.length - 1;
                        const endCol = startCol + data[0].length - 1;

                        const range = {
                            row: [startRow, endRow],
                            column: [startCol, endCol]
                        };

                        workbookInstance.setCellValuesByRange(data, range);
                    }
                }
            }).catch(err => {
                console.error("Paste failed:", err);
                alert(t("Please allow clipboard access or use Ctrl+V"));
            });
        } else if (name === 'find') {
            focusSheet();
            container.dispatchEvent(new KeyboardEvent('keydown', { key: 'f', code: 'KeyF', ctrlKey: true, bubbles: true, cancelable: true }));

        } else if (name === 'replace') {
            focusSheet();
            container.dispatchEvent(new KeyboardEvent('keydown', { key: 'h', code: 'KeyH', ctrlKey: true, bubbles: true, cancelable: true }));
        } else if (name === 'select-all') {
            focusSheet();
            container.dispatchEvent(new KeyboardEvent('keydown', { key: 'a', code: 'KeyA', ctrlKey: true, bubbles: true, cancelable: true }));
        } else if (name === 'row-height') {
            const selections = workbookInstance?.getSelection();
            if (selections && selections.length > 0) {
                const height = prompt(t("Enter row height (px):"), "20");
                if (height && !isNaN(height)) {
                    const rowInfo = {};
                    const range = selections[0];
                    if (range.row) {
                        const start = range.row[0];
                        const end = range.row[1];
                        for (let r = start; r <= end; r++) {
                            rowInfo[r] = parseInt(height, 10);
                        }
                        if (workbookInstance.setRowHeight) {
                            workbookInstance.setRowHeight(rowInfo);
                        }
                    }
                }
            } else {
                alert(t("Please select rows first"));
            }
        } else if (name === 'col-width') {
            const selections = workbookInstance?.getSelection();
            if (selections && selections.length > 0) {
                const width = prompt(t("Enter column width (px):"), "100");
                if (width && !isNaN(width)) {
                    const colInfo = {};
                    const range = selections[0];
                    if (range.column) {
                        const start = range.column[0];
                        const end = range.column[1];
                        for (let c = start; c <= end; c++) {
                            colInfo[c] = parseInt(width, 10);
                        }
                        if (workbookInstance.setColumnWidth) {
                            workbookInstance.setColumnWidth(colInfo);
                        }
                    }
                }
            } else {
                alert(t("Please select columns first"));
            }
        } else if (name === 'bold') {
            container.dispatchEvent(new KeyboardEvent('keydown', { key: 'b', code: 'KeyB', ctrlKey: true, bubbles: true, cancelable: true }));
        }
    };

    if (loading) return <Segment basic loading />;

    // Custom items for toolbar to match image (Save, Cut, Copy, Paste)
    const customToolbarItems = readOnly ? [] : [
        // Cut/Copy/Paste usually require clipboard permissions or system events
        // Adding visual buttons to match image
        {
            key: "cut",
            tooltip: t("Cut"),
            onClick: () => handleMenuClick(null, { name: 'cut' }),
            icon: <Icon name="cut" />
        },
        {
            key: "copy",
            tooltip: t("Copy"),
            onClick: () => handleMenuClick(null, { name: 'copy' }),
            icon: <Icon name="copy" />
        },
        {
            key: "paste",
            tooltip: t("Paste"),
            onClick: () => handleMenuClick(null, { name: 'paste' }),
            icon: <Icon name="paste" />
        }
    ];

    if (!hideAction) {
        customToolbarItems.unshift({
            key: "save",
            tooltip: iconName === "save" ? t("Save") : t("Upload"),
            onClick: onBlur,
            icon: <Icon name={iconName} />
        });
    }

    // Standard items to match image
    const toolbarItems = [
        'undo',
        'redo',
        "format-painter",
        'font-size',
        'bold',
        'italic',
        'underline',
        'font-color',
        'background',
        'horizontal-align',
        'number-decrease',
        'number-increase',
        'text-wrap',
        "currency-format",
        "percentage-format",
        "format",
    ];

    return (
        <div id="parentSheet" style={{ width: "100%", height: "100%", display: 'flex', flexDirection: 'column' }}>
            {/* Custom Menu Bar Matching Image 2 & 3 */}
            {/* Workbook with Toolbar */}
            <div id="excel-container" style={{ flex: 1, position: 'relative' }}>
                {formulaError && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        padding: '8px 16px',
                        fontSize: '14px',
                        zIndex: 1000,
                        borderBottom: '2px solid #c62828',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <Icon name="warning circle" /> {formulaError}
                    </div>
                )}                

                <Workbook
                    ref={ref}
                    data={innerData}
                    allowEdit={!readOnly}
                    column={40}
                    row={300}
                    defaultColWidth={150}
                    showInfobar={false} // Hide default title bar
                    showToolbar={true}
                    toolbarItems={toolbarItems}
                    customToolbarItems={customToolbarItems}
                    showSheetTabs={false}
                    lang="en"
                    onChange={handleChange}
                />
            </div>
        </div>
    );
};

export default SpreadsheetEditor;